import type { Role } from '@aisolution/shared';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: Role;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    ...init,
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
  return data as T;
}

export const api = {
  setupStatus: () => req<{ needsSetup: boolean }>('/auth/setup/status'),
  setup: (b: { email: string; name: string; password: string }) =>
    req<SessionUser>('/auth/setup', { method: 'POST', body: JSON.stringify(b) }),
  login: (b: { email: string; password: string }) =>
    req<SessionUser>('/auth/login', { method: 'POST', body: JSON.stringify(b) }),
  logout: () => req<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => req<SessionUser>('/auth/me'),
  listProjects: () => req<{ projects: ProjectRow[] }>('/projects'),
  createProject: (b: Record<string, string>) =>
    req<ProjectRow>('/projects', { method: 'POST', body: JSON.stringify(b) }),
  getProject: (id: string) => req<ProjectRow>(`/projects/${id}`),
  ingest: (id: string, b: { filename: string; pages: { page: number; text: string }[] }) =>
    req<{ ok: true; source: 'gemini' | 'claude' | 'sample'; counts: Record<string, number> }>(
      `/projects/${id}/ingest`, { method: 'POST', body: JSON.stringify(b) }),
  getAnalysis: (id: string) => req<AnalysisPayload>(`/projects/${id}/analysis`),
  toggleSystem: (id: string, systemId: string, selected: boolean) =>
    req<{ ok: true; selected: boolean }>(`/projects/${id}/systems/${systemId}`,
      { method: 'PUT', body: JSON.stringify({ selected }) }),
  getMappings: (id: string) => req<MappingPayload>(`/projects/${id}/mappings`),
  proposeMappings: (id: string) =>
    req<{ ok: true; total: number; needs_review: number; gate: MappingGate }>(
      `/projects/${id}/mappings/propose`, { method: 'POST' }),
  updateMapping: (id: string, symbolId: string, b: { device_key?: string | null; status?: MappingStatus }) =>
    req<{ ok: true; gate: MappingGate }>(`/projects/${id}/mappings/${symbolId}`,
      { method: 'PUT', body: JSON.stringify(b) }),
  editField: (id: string, b: { field: string; value?: string; status?: 'confirmed' | 'edited' }) =>
    req<{ ok: true }>(`/projects/${id}/extraction`, { method: 'PUT', body: JSON.stringify(b) }),
  getCountSheet: (id: string) => req<CountSheetPayload>(`/projects/${id}/count-sheet`),
  importCounts: (id: string, rows: { symbol_id: string; qty: string; comments: string }[]) =>
    req<{ ok: true; report: ValidationReport }>(`/projects/${id}/counts`,
      { method: 'POST', body: JSON.stringify({ rows }) }),
  generateBom: (id: string) => req<{ ok: true; result: BomResult }>(`/projects/${id}/bom/generate`, { method: 'POST' }),
  getBom: (id: string) => req<BomPayload>(`/projects/${id}/bom`),
  getCosting: (id: string) => req<Costing>(`/projects/${id}/costing`),
  repriceBom: (id: string) => req<{ ok: true; summary: RepriceSummary }>(`/projects/${id}/bom/reprice`, { method: 'POST' }),
  uploadPrices: (id: string, rows: Record<string, unknown>[]) =>
    req<{ ok: true; inserted: number; skipped: number }>(`/projects/${id}/prices`, { method: 'POST', body: JSON.stringify({ rows }) }),
  saveWageSettings: (id: string, b: { wage_type?: string; state?: string; county?: string; shift?: string }) =>
    req<{ ok: true }>(`/projects/${id}/wage-settings`, { method: 'PUT', body: JSON.stringify(b) }),
  getTravel: (id: string) => req<{ travel: TravelRow[] }>(`/projects/${id}/travel`),
  addTravel: (id: string, b: Record<string, unknown>) => req<{ ok: true; id: string }>(`/projects/${id}/travel`, { method: 'POST', body: JSON.stringify(b) }),
  deleteTravel: (id: string, tid: string) => req<{ ok: true }>(`/projects/${id}/travel/${tid}`, { method: 'DELETE' }),
  getMarkups: (id: string) => req<{ markups: MarkupRow[]; defaults: { name: string; label: string; pct: number; basis: string }[] }>(`/projects/${id}/markups`),
  saveMarkups: (id: string, markups: { name: string; pct: number }[]) =>
    req<{ ok: true }>(`/projects/${id}/markups`, { method: 'PUT', body: JSON.stringify({ markups }) }),
  generateRfis: (id: string) => req<{ ok: true; count: number }>(`/projects/${id}/rfis/generate`, { method: 'POST' }),
  getRfis: (id: string) => req<{ rfis: RfiRow[] }>(`/projects/${id}/rfis`),
  generateChangeOrders: (id: string) => req<{ ok: true; count: number }>(`/projects/${id}/change-orders/generate`, { method: 'POST' }),
  getChangeOrders: (id: string) => req<{ change_orders: CoRow[] }>(`/projects/${id}/change-orders`),
  saveEstimate: (id: string) => req<{ ok: true; version: number; final_sell_price: number }>(`/projects/${id}/estimate`, { method: 'POST' }),
  getEstimate: (id: string) => req<{ estimate: EstimateRow | null }>(`/projects/${id}/estimate`),
  pdfUrl: (id: string, kind: 'supplier-pdf' | 'estimate-pdf' | 'proposal-short-pdf' | 'proposal-full-pdf') => `/api/projects/${id}/${kind}`,
  getQcReview: (id: string) => req<QcReview>(`/projects/${id}/qc-review`),
  // Admin
  getCompanyProfile: () => req<{ sections: CompanySection[] }>(`/admin/company-profile`),
  updateCompanyProfile: (section: string, b: { title?: string; body?: string }) =>
    req<{ ok: true }>(`/admin/company-profile/${section}`, { method: 'PUT', body: JSON.stringify(b) }),
  getPersonnel: () => req<{ personnel: Personnel[] }>(`/admin/personnel`),
  addPersonnel: (b: { name: string; role: string }) => req<{ ok: true; id: string }>(`/admin/personnel`, { method: 'POST', body: JSON.stringify(b) }),
  deletePersonnel: (pid: string) => req<{ ok: true }>(`/admin/personnel/${pid}`, { method: 'DELETE' }),
  getCodes: () => req<{ codes: CodeLibRow[] }>(`/admin/codes`),
  addCode: (b: { level: string; code: string; title?: string; applies_to_system?: string }) =>
    req<{ ok: true; id: string }>(`/admin/codes`, { method: 'POST', body: JSON.stringify(b) }),
};

export interface QcCheck { key: string; label: string; status: 'pass' | 'warn' | 'fail'; detail: string }
export interface QcReview { checks: QcCheck[]; summary: { pass: number; warn: number; fail: number } }
export interface CompanySection { section: string; title: string; body: string; sort: number; is_placeholder: number }
export interface Personnel { id: string; name: string; role: string; active: number }
export interface CodeLibRow { id: string; level: string; code: string; title: string; applies_to_system: string | null; certainty: string }

export interface RfiRow { id: string; number: number | null; title: string | null; question: string | null; reason: string | null; source_ref: string | null; cost_schedule_impact: string | null; suggested_clarification: string | null; status: string | null }
export interface CoRow { id: string; title: string | null; description: string | null; why: string | null; source_ref: string | null; affected_system: string | null; risk_level: string | null; est_cost_impact_placeholder: string | null; est_schedule_impact_placeholder: string | null; recommendation: string | null }
export interface EstimateRow { id: string; version: number; final_sell_price: number; created_at: string; costing: Costing }

export interface RepriceSummary { repriced: number; still_internet: number; stale: number; by_source: Record<string, number> }
export interface TravelRow { id: string; type: string; qty: number | null; unit_cost: number | null; crew_size: number | null; days: number | null; distance_mi: number | null; notes: string | null }
export interface MarkupRow { name: string; pct_or_amount: number; basis: string | null }
export interface Costing {
  wage: { type: string; state: string | null; county: string | null; shift: string };
  material_cost: number; labor_hours: number; labor_cost: number;
  by_category: { category: string; classification: string; hours: number; rate: number; cost: number; rate_source: string | null; is_sample: boolean }[];
  travel_total: number;
  markups: { name: string; pct: number; amount: number }[];
  material_markup: number; overhead: number; profit: number; sales_tax: number; freight: number;
  small_tools: number; contingency: number; bond: number; direct_cost: number; sell_price: number; warnings: string[];
}

export interface BomResult { lines: number; total_material: number; total_labor_hours: number; no_assembly: string[]; uncounted: string[] }
export interface BomLine {
  id: string; system: string | null; symbol_id: string | null; part_number: string | null; manufacturer: string | null;
  qty: number | null; unit: string | null; description: string | null;
  unit_material_cost: number | null; extended_material_cost: number | null;
  labor_unit_hours: number | null; extended_labor_hours: number | null; labor_category: string | null;
  price_source: string | null; confidence_score: number | null; source_reference: string | null; notes: string | null;
}
export interface BomPayload { lines: BomLine[]; totals: { material: number; labor_hours: number; line_count: number } }

export interface CountSheetRowData {
  symbol_id: string; system_name: string | null; symbol_tag: string | null; legend_description: string | null;
  device_type: string | null; spec_reference: string | null; sheets_present: string | null;
  assembly_hint: string | null; confidence: number | null; map_status: string | null; count: number | null;
}
export interface CountSheetPayload { project: { id: string; name: string }; rows: CountSheetRowData[] }
export interface ValidationReport {
  total_symbols: number; rows_received: number; accepted: number;
  blanks: string[]; negatives: string[]; non_numeric: string[]; unmatched: string[]; duplicates: string[]; ok: boolean;
}

export type MappingStatus = 'proposed' | 'confirmed' | 'needs_review' | 'excluded';
export interface MappingRow {
  symbol_id: string; device_key: string | null; device_type: string | null; assembly_hint: string | null;
  confidence: number; status: MappingStatus; source: string;
  symbol_tag: string | null; legend_description: string | null; system_name: string | null; sheets_present: string | null;
}
export interface MappingGate {
  total: number; confirmed: number; proposed: number; needs_review: number; excluded: number; blocks_bom: boolean;
}
export interface DeviceOption { key: string; label: string; system: string; assemblyHint: string }
export interface MappingPayload { mappings: MappingRow[]; gate: MappingGate; devices: DeviceOption[] }

export interface ExtractionRow { field: string; value: string; source_ref: string; confidence: number; status: string }
export interface SystemRow {
  id: string; division: number; system_code: string | null; system_name: string;
  selected: number; auto_selected: number; confidence: number | null; evidence: string | null;
}
export interface SymbolRow {
  id: string; system_id: string | null; symbol_tag: string | null;
  legend_description: string | null; spec_reference: string | null; sheets_present: string | null;
}
export interface CodeRow { level: string; code: string; title: string; applies_to_system: string | null; certainty: string; source_ref: string }
export interface AnalysisPayload {
  project: ProjectRow;
  extraction: ExtractionRow[];
  systems: SystemRow[];
  symbols: SymbolRow[];
  codes: CodeRow[];
  documents: { filename: string; uploaded_at: string }[];
}

export interface ProjectRow {
  id: string;
  name: string;
  customer_name: string | null;
  gc_name: string | null;
  county: string | null;
  state: string | null;
  bid_date: string | null;
  bid_due_date?: string | null;
  architect?: string | null;
  engineer?: string | null;
  pw_jurisdiction?: string | null;
  davis_bacon?: number | null;
  estimator: string | null;
  salesperson: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}
